/**
 * bridge/app_lifecycle_controller.js — "app-lifecycle" bridge component
 * (Stimulus + Strada).
 *
 * Reports whether the app is in the foreground and PUSHES every transition to
 * the web. Stimulus identifier "bridge--app-lifecycle" (glob-derived from this
 * subdir); Strada component name "app-lifecycle" MUST match the iOS class var
 * name (native/ios/Chippie/Bridge/AppLifecycleComponent.swift).
 *
 * Why the web cannot do this itself: a WebView inside a backgrounded native app
 * does NOT reliably fire visibilitychange, and `document.hidden` can stay false
 * for a page nobody has looked at in three hours. Any web code that polls, holds
 * an EventSource, runs a job timer, or shows data that goes stale needs the real
 * native answer. This is a SUBSCRIPTION, like bridge--network-status: the native
 * side pushes on every transition.
 *
 * The state is published two ways on every platform, so consumers never poll:
 *   1. document.documentElement.dataset.appState = "active" | "background"
 *      (readable synchronously from anywhere, usable as a CSS hook).
 *   2. a bubbling document event "chippie:app-lifecycle" with
 *        detail: { state: "active" | "background", awayMs: Number }
 *      `awayMs` is how long the app spent in the background before this
 *      "active" (0 for a background transition, and 0 on the first ever
 *      "active"). It is what a "you were away, reload stale data" check reads.
 *
 * API — window.ChippieBridge.appLifecycle:
 *   .state()        -> "active" | "background" (current, synchronous)
 *   .subscribe(cb)  -> cb({ state, awayMs }) on every transition; returns an
 *                      unsubscribe function. Sugar over the document event for
 *                      callers that would rather not manage a listener.
 * Both methods read module state, so they are identical on every platform; only
 * the transport that KEEPS that state fresh differs.
 *
 * Three-way transport (facade SSOT, chippie-bridge.js owns platform detection):
 *   - ios:     this.send("connect", {}, cb) once; AppLifecycleComponent replies
 *              again on each UIApplication did-become-active / did-enter-background.
 *   - android: window.ChippieNative.startAppLifecycle(); MainActivity's
 *              onResume/onPause push via window.__chippieAppLifecycle(state).
 *   - pwa:     document visibilitychange + window pageshow/pagehide. Weaker than
 *              native (a backgrounded tab may not report promptly) but correct
 *              for the cases a browser does surface.
 *
 * Server-drivable: only `appLifecycle.state` is in REMOTE_ALLOWED — a pure read
 * with no side effect. `subscribe` is not, because a server-installed callback
 * has nowhere to deliver to.
 *
 * Template wiring (one instance — it is a global subscription; shell layout):
 *   <div data-controller="bridge--app-lifecycle"></div>
 *
 * Outside a native shell BridgeComponent never connect()s, so the PWA path is
 * wired standalone at module load and connect() upgrades to Strada on iOS.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Global name the Android side calls on every transition. Kept in sync with the
// Kotlin handler (MainActivity.emitAppLifecycle).
const ANDROID_PUSH_CALLBACK = "__chippieAppLifecycle";

let current = "active";
let backgroundedAt = null;
const subscribers = new Set();

// Publish a transition everywhere. Repeat states are dropped: iOS and Android
// both emit near-duplicate foreground events (didBecomeActive fires after
// willEnterForeground), and a consumer that refetches on every one of those
// would fire twice per resume.
function publish(state) {
    const next = state === "background" ? "background" : "active";
    if (next === current) return;

    let awayMs = 0;
    if (next === "background") {
        backgroundedAt = Date.now();
    } else if (backgroundedAt !== null) {
        awayMs = Date.now() - backgroundedAt;
        backgroundedAt = null;
    }

    current = next;
    document.documentElement.dataset.appState = current;

    const detail = { state: current, awayMs };
    subscribers.forEach((cb) => {
        try {
            cb({ ...detail });
        } catch (_e) {
            // A throwing subscriber must not stop the rest from being told.
        }
    });
    document.dispatchEvent(
        new CustomEvent("chippie:app-lifecycle", { detail, bubbles: true })
    );
}

let pwaWired = false;
function startPwa() {
    if (pwaWired) return;
    pwaWired = true;
    document.documentElement.dataset.appState = current;
    const sync = () => publish(document.hidden ? "background" : "active");
    document.addEventListener("visibilitychange", sync);
    // pageshow/pagehide catch the bfcache round trip, which visibilitychange
    // alone misses on iOS Safari.
    window.addEventListener("pageshow", () => publish("active"));
    window.addEventListener("pagehide", () => publish("background"));
}

let androidWired = false;
function startAndroid() {
    if (androidWired) return;
    if (!window.ChippieNative || !window.ChippieNative.startAppLifecycle) return;
    androidWired = true;
    window[ANDROID_PUSH_CALLBACK] = (state) => publish(state);
    try {
        window.ChippieNative.startAppLifecycle();
    } catch (_e) {
        androidWired = false;
    }
}

// Same implementation on every platform: both methods read module state, which
// whichever transport is running keeps current.
function api() {
    return {
        state: () => current,
        subscribe: (cb) => {
            if (typeof cb !== "function") return () => {};
            subscribers.add(cb);
            return () => subscribers.delete(cb);
        },
    };
}

function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    const impl = api();
    window.ChippieBridge.register("appLifecycle", { ios: impl, android: impl, pwa: impl });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

// Start the transports that need no Strada connect. The PWA listeners are always
// wired (harmless in a shell, and the only source in a browser); the Android push
// supersedes them when the native interface is present.
function startStandaloneTransports() {
    startPwa();
    startAndroid();
}
if (document.readyState !== "loading") {
    startStandaloneTransports();
} else {
    document.addEventListener("DOMContentLoaded", startStandaloneTransports, { once: true });
}

export default class extends BridgeComponent {
    static component = "app-lifecycle";

    connect() {
        super.connect();
        // One "connect" subscription; the callback re-fires on every native
        // transition for as long as this component is attached.
        this.send("connect", {}, (message) => {
            publish((message.data || {}).state);
        });
    }
}
